import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
  Stack,
  Tooltip,
  Tabs,
  Tab,
  Pagination,
  Alert,
} from "@mui/material";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { useAuth } from "../../context/AuthContext";
import { markNotificationAsRead, getAdminNotifications } from "../../api/auth";
import DashboardLayout from "../../layout/DashboardLayout";
import { brand } from "../../theme";
import { INTERNAL_ROLES } from "../../constants/roles";
import { EmptyState } from "../../components/EmptyState";

export default function AdminNotificationsPage() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [notifPage, setNotifPage] = useState(1);

  const allowedRoles = INTERNAL_ROLES;

  useEffect(() => {
    if (!user || !allowedRoles.includes(user.role)) return;

    (async () => {
      try {
        const res = await getAdminNotifications();
        setNotifications(res.data.notifications);
      } catch (err) {
        console.error(err);
        setError("Failed to load notifications. Please refresh the page and try again.");
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  if (!user || !allowedRoles.includes(user.role)) {
    return (
      <Box p={3}>
        <Typography color="error">Unauthorized Access</Typography>
      </Box>
    );
  }

  const filteredNotifications =
    filter === "all"
      ? notifications
      : notifications.filter((n) => n.type === filter);

  const markAsRead = async (id) => {
    try {
      await markNotificationAsRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n._id === id ? { ...n, read: true } : n))
      );
    } catch (err) {
      console.error(err);
    }
  };

  const typeColors = {
    signup: "primary",
    pcc_request: "info",
    product_request: "success",
    demo_request: "secondary",
    kyc: "warning",
    contract_signed: "success",
  };

  return (
    <DashboardLayout>
      <Box p={3}>
        <Box
          mb={3}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h5" fontWeight="bold">
            Notifications
          </Typography>

          <NotificationsIcon sx={{ color: brand.terracotta, fontSize: 32 }} />
        </Box>

        {/* Filters */}
        <Tabs
          value={filter}
          onChange={(e, val) => { setFilter(val); setNotifPage(1); }}
          sx={{ mb: 3, borderBottom: "1px solid #e5e7eb" }}
        >
          <Tab value="all" label="All" />
          <Tab value="signup" label="Signups" />
          <Tab value="pcc_request" label="PCC Requests" />
          <Tab value="product_request" label="Product Requests" />
          <Tab value="demo_request" label="Demo Requests" />
          <Tab value="kyc" label="KYC" />
        </Tabs>

        {loading && (
          <Box display="flex" justifyContent="center" mt={5}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error">{error}</Alert>
        )}

        {!loading && !error && filteredNotifications.length === 0 && (
          <EmptyState
            icon={<NotificationsIcon sx={{ fontSize: "2.5rem", color: "#cbd5e1" }} />}
            description={
              filter === "all"
                ? "No notifications yet."
                : "No notifications match this filter."
            }
            action={
              filter !== "all"
                ? { label: "Clear filter", onClick: () => { setFilter("all"); setNotifPage(1); } }
                : undefined
            }
          />
        )}

        <Stack spacing={2}>
          {filteredNotifications.slice((notifPage - 1) * 10, notifPage * 10).map((item) => (
            <Card
              key={item._id}
              sx={{
                bgcolor: item.read ? "#f8fafc" : brand.terracottaTint,
                borderLeft: `4px solid ${item.read ? "#94a3b8" : brand.terracotta}`,
              }}
            >
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    <Chip
                      label={item.type.replace("_", " ").toUpperCase()}
                      color={typeColors[item.type] || "default"}
                      size="small"
                      sx={{ mb: 1 }}
                    />

                    <Typography variant="subtitle1" fontWeight="bold">
                      {item.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.message}
                    </Typography>
                  </Box>

                  <Tooltip title="Mark as Read">
                    <IconButton
                      onClick={() => markAsRead(item._id)}
                      disabled={item.read}
                    >
                      <MarkEmailReadIcon
                        color={item.read ? "disabled" : "primary"}
                      />
                    </IconButton>
                  </Tooltip>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
        {Math.ceil(filteredNotifications.length / 10) > 1 && (
          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
              count={Math.ceil(filteredNotifications.length / 10)}
              page={notifPage}
              onChange={(_, v) => setNotifPage(v)}
              color="primary"
            />
          </Box>
        )}
      </Box>
    </DashboardLayout>
  );
}
